import { SellsService } from '@/services/sells.service';
import { useCallback, useEffect, useMemo } from 'react';
import type {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

export type SoldFilterOption = { value: string; label: string };

function asFilterOptions(value: unknown): SoldFilterOption[] {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is SoldFilterOption =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as SoldFilterOption).value === 'string' &&
      typeof (item as SoldFilterOption).label === 'string',
  );
}

interface UseSoldProductFilterOptionsParams {
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  currency: string;
  scope: string;
  enableCheckouts?: boolean;
  enableAffiliates?: boolean;
  enableCoproducers?: boolean;
}

export function useSoldProductFilterOptions({
  watch,
  setValue,
  getValues,
  currency,
  scope,
  enableCheckouts = false,
  enableAffiliates = true,
  enableCoproducers = true,
}: UseSoldProductFilterOptionsParams) {
  const selectedProducts = watch('products');
  const productIds = useMemo(
    () => asFilterOptions(selectedProducts).map((p) => p.value),
    [selectedProducts],
  );
  const productIdsKey = useMemo(
    () => [...productIds].sort().join(','),
    [productIds],
  );

  const selectedOffers = watch('offers');
  const offerIds = useMemo(
    () => asFilterOptions(selectedOffers).map((o) => o.value),
    [selectedOffers],
  );
  const offerIdsKey = useMemo(
    () => [...offerIds].sort().join(','),
    [offerIds],
  );

  const fetchProductOptions = useCallback(
    async (query?: string) => SellsService.getSoldProducts(query, currency),
    [currency],
  );

  const fetchOfferOptions = useCallback(
    async (query?: string) => {
      if (!productIds.length) return [];
      return (
        (await SellsService.getSoldOffers({
          productIds,
          query,
          currency,
        })) || []
      );
    },
    [productIds, currency],
  );

  const fetchCheckoutsOptions = useCallback(
    async (query?: string) => {
      if (!enableCheckouts || !productIds.length || !offerIds.length) {
        return [];
      }
      return SellsService.getSoldCheckouts({
        productIds,
        offerIds,
        query,
        currency,
      });
    },
    [enableCheckouts, productIds, offerIds, currency],
  );

  const fetchAffiliatesOptions = useCallback(
    async (query?: string) => {
      if (!enableAffiliates) return [];
      if (productIds.length) {
        return SellsService.getSoldAffiliates({ productIds, query });
      }
      return SellsService.getPartnersByRole('affiliate', query);
    },
    [enableAffiliates, productIds],
  );

  const fetchCoProducersOptions = useCallback(
    async (query?: string) => {
      if (!enableCoproducers) return [];
      if (productIds.length) {
        return SellsService.getSoldCoproducers({ productIds, query });
      }
      return SellsService.getPartnersByRole('coproducer', query);
    },
    [enableCoproducers, productIds],
  );

  useEffect(() => {
    if (!productIds.length) return;

    let active = true;

    const syncOffers = async () => {
      const availableOffers = await fetchOfferOptions();
      if (!active) return;

      const availableOfferIds = new Set(
        availableOffers.map((o) => o.value),
      );
      const currentOffers = asFilterOptions(getValues('offers'));
      const filteredOffers = currentOffers.filter((o) =>
        availableOfferIds.has(o.value),
      );
      if (filteredOffers.length !== currentOffers.length) {
        setValue('offers', filteredOffers);
      }
    };

    syncOffers();

    return () => {
      active = false;
    };
  }, [productIdsKey, productIds.length, fetchOfferOptions, getValues, setValue]);

  useEffect(() => {
    if (!enableCheckouts || !offerIds.length || !productIds.length) return;

    let active = true;

    const syncCheckouts = async () => {
      const availableCheckouts = await fetchCheckoutsOptions();
      if (!active) return;

      const availableCheckoutIds = new Set(
        availableCheckouts.map((c) => c.value),
      );
      const currentCheckouts = asFilterOptions(getValues('checkouts'));
      const filteredCheckouts = currentCheckouts.filter((c) =>
        availableCheckoutIds.has(c.value),
      );
      if (filteredCheckouts.length !== currentCheckouts.length) {
        setValue('checkouts', filteredCheckouts);
      }
    };

    syncCheckouts();

    return () => {
      active = false;
    };
  }, [
    enableCheckouts,
    offerIdsKey,
    productIdsKey,
    offerIds.length,
    productIds.length,
    fetchCheckoutsOptions,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!enableAffiliates && !enableCoproducers) return;

    const currentAffiliates = enableAffiliates
      ? asFilterOptions(getValues('affiliates'))
      : [];
    const currentCoproducers = enableCoproducers
      ? asFilterOptions(getValues('coproducers'))
      : [];

    if (!currentAffiliates.length && !currentCoproducers.length) {
      return;
    }

    let active = true;

    const syncPartners = async () => {
      const [availableAffiliates, availableCoproducers] = await Promise.all([
        enableAffiliates
          ? productIds.length
            ? SellsService.getSoldAffiliates({ productIds })
            : SellsService.getPartnersByRole('affiliate')
          : Promise.resolve([]),
        enableCoproducers
          ? productIds.length
            ? SellsService.getSoldCoproducers({ productIds })
            : SellsService.getPartnersByRole('coproducer')
          : Promise.resolve([]),
      ]);

      if (!active) return;

      if (currentAffiliates.length) {
        const availableAffiliateIds = new Set(
          availableAffiliates.map((a) => a.value),
        );
        const filteredAffiliates = currentAffiliates.filter((a) =>
          availableAffiliateIds.has(a.value),
        );
        if (filteredAffiliates.length !== currentAffiliates.length) {
          setValue('affiliates', filteredAffiliates);
        }
      }

      if (currentCoproducers.length) {
        const availableCoproducerIds = new Set(
          availableCoproducers.map((c) => c.value),
        );
        const filteredCoproducers = currentCoproducers.filter((c) =>
          availableCoproducerIds.has(c.value),
        );
        if (filteredCoproducers.length !== currentCoproducers.length) {
          setValue('coproducers', filteredCoproducers);
        }
      }
    };

    syncPartners();

    return () => {
      active = false;
    };
  }, [
    enableAffiliates,
    enableCoproducers,
    productIdsKey,
    productIds,
    getValues,
    setValue,
  ]);

  const handleProductsChange = useCallback(
    (value: SoldFilterOption[]) => {
      setValue('products', value);
      setValue('offers', []);
      if (enableCheckouts) {
        setValue('checkouts', []);
      }
    },
    [enableCheckouts, setValue],
  );

  const handleOffersChange = useCallback(
    (values: SoldFilterOption[]) => {
      setValue('offers', values);
      if (enableCheckouts) {
        setValue('checkouts', []);
      }
    },
    [enableCheckouts, setValue],
  );

  return {
    productIds,
    productIdsKey,
    offerIds,
    offerIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchCheckoutsOptions,
    fetchAffiliatesOptions,
    fetchCoProducersOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys: {
      offers: `${scope}-offers`,
      checkouts: `${scope}-checkouts`,
      affiliates: `${scope}-affiliates`,
      coproducers: `${scope}-coproducers`,
    },
  };
}
